import { parseGameSpec } from '../../games/spec/schema.ts';
import type { GameSpec } from '../../games/spec/schema.ts';
import type { PlayerPublic } from '../../shared/protocol.ts';

export interface Me {
  readonly authenticated: true;
  readonly userId: string;
  readonly displayName: string;
  /** Null for guests and email-only accounts. */
  readonly login: string | null;
  readonly email: string | null;
  /** True while playing without an account: progress is tied to a cookie. */
  readonly isGuest: boolean;
  readonly rating: number;
  /** The record behind the rating, so "your games came with you" is visible. */
  readonly games: number;
  readonly wins: number;
  readonly losses: number;
  readonly draws: number;
}

export interface LeaderboardEntry {
  readonly userId: string;
  readonly displayName: string;
  readonly rating: number;
  readonly games: number;
  readonly wins: number;
  readonly losses: number;
  readonly draws: number;
}

/** Resolves null when signed out. A 401 here is expected, not an error. */
export async function fetchMe(signal?: AbortSignal): Promise<Me | null> {
  const response = await fetch('/api/me', {
    credentials: 'same-origin',
    ...(signal ? { signal } : {}),
  });
  if (response.status === 401) return null;
  if (!response.ok) throw new Error(`/api/me failed with ${response.status}`);
  return (await response.json()) as Me;
}

export async function fetchLeaderboard(signal?: AbortSignal): Promise<LeaderboardEntry[]> {
  const response = await fetch('/api/leaderboard?limit=10', {
    credentials: 'same-origin',
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) throw new Error(`/api/leaderboard failed with ${response.status}`);
  const body = (await response.json()) as { entries: LeaderboardEntry[] };
  return body.entries;
}

export async function logout(): Promise<void> {
  await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
}

/* --------------------------------------------------------------------------- *
 * The game catalogue. Reads are public: playing is free, authoring is what the
 * subscription buys.
 * --------------------------------------------------------------------------- */

export interface CatalogueEntry {
  readonly gameId: string;
  readonly version: number;
  readonly title: string;
  readonly publishedAt: string;
}

export async function fetchGames(signal?: AbortSignal): Promise<CatalogueEntry[]> {
  const response = await fetch('/api/games', {
    credentials: 'same-origin',
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) throw new Error(`/api/games failed with ${response.status}`);
  const body = (await response.json()) as { entries: CatalogueEntry[] };
  return body.entries;
}

/**
 * The published spec for one game — the rules the client renders and validates
 * frames against.
 *
 * Parsed with the real schema rather than trusted as JSON. The board geometry
 * from this call drives array indexing and the win-line overlay, so a malformed
 * response would otherwise surface as a rendering bug far from its cause.
 */
export async function fetchGameSpec(gameId: string, signal?: AbortSignal): Promise<GameSpec> {
  const response = await fetch(`/api/games/${encodeURIComponent(gameId)}`, {
    credentials: 'same-origin',
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) throw new Error(`/api/games/${gameId} failed with ${response.status}`);

  const body = (await response.json()) as { spec: unknown };
  const parsed = parseGameSpec(body.spec);
  if (!parsed.ok) {
    const detail = parsed.issues.map((issue) => `${issue.path}: ${issue.message}`).join('; ');
    throw new Error(`server sent an invalid spec for '${gameId}': ${detail}`);
  }
  return parsed.spec;
}

/* --------------------------------------------------------------------------- *
 * Post-game review. Public, like the stream the game was played on.
 * --------------------------------------------------------------------------- */

export interface ReviewMove {
  readonly moveNumber: number;
  readonly seat: number;
  readonly kind: 'drop' | 'resign' | 'abandon';
  /** Null for a move that ended the match without touching the board. */
  readonly param: number | null;
}

export interface MatchReview {
  readonly matchId: string;
  readonly game: string;
  readonly spec: GameSpec;
  readonly firstPlayerSeat: number;
  readonly seats: Record<string, PlayerPublic>;
  readonly startedAt: string;
  readonly endedAt: string | null;
  readonly result: 'seat1_win' | 'seat2_win' | 'draw' | null;
  readonly endReason: string | null;
  readonly moves: readonly ReviewMove[];
}

/** Why a review could not be shown, in terms the screen can put into words. */
export type ReviewFailure = 'not_found' | 'still_in_play' | 'unavailable';

export class MatchReviewError extends Error {
  readonly reason: ReviewFailure;

  constructor(reason: ReviewFailure, message: string) {
    super(message);
    this.name = 'MatchReviewError';
    this.reason = reason;
  }
}

/**
 * The move log of a finished match.
 *
 * The spec goes through `parseGameSpec` for the same reason `fetchGameSpec`
 * does: the board geometry drives array indexing, the win-line overlay and the
 * analysis engine's own tables, so a malformed response has to fail here rather
 * than as a rendering bug three modules away.
 */
export async function fetchMatchReview(
  matchId: string,
  signal?: AbortSignal,
): Promise<MatchReview> {
  const response = await fetch(`/api/matches/${encodeURIComponent(matchId)}/review`, {
    credentials: 'same-origin',
    ...(signal ? { signal } : {}),
  });

  if (response.status === 404) {
    throw new MatchReviewError('not_found', 'That match does not exist.');
  }
  if (response.status === 409) {
    throw new MatchReviewError('still_in_play', 'That match is still being played.');
  }
  if (!response.ok) {
    throw new MatchReviewError('unavailable', `review failed with ${String(response.status)}`);
  }

  const body = (await response.json()) as Omit<MatchReview, 'spec'> & { spec: unknown };
  const parsed = parseGameSpec(body.spec);
  if (!parsed.ok) {
    const detail = parsed.issues.map((issue) => `${issue.path}: ${issue.message}`).join('; ');
    throw new MatchReviewError('unavailable', `server sent an invalid spec: ${detail}`);
  }
  return { ...body, spec: parsed.spec };
}

/**
 * Starts playing with no account.
 *
 * Idempotent server-side: calling it while already signed in returns the
 * existing identity rather than replacing it, so a stray call can never log
 * somebody out of a real account.
 */
export type AuthResult = { ok: true; me: Me } | { ok: false; message: string };

/** Maps the server's error codes to something a person can act on. */
function authMessage(status: number, code: unknown): string {
  if (status === 409 || code === 'email_taken') return 'That email is already registered.';
  if (status === 401) return 'Email or password is incorrect.';
  if (status === 429) return 'Too many attempts. Wait a minute and try again.';
  if (code === 'invalid_credentials') return 'Check the email and a password of at least 10 characters.';
  return 'Something went wrong. Try again.';
}

async function postCredentials(path: string, email: string, password: string): Promise<AuthResult> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: unknown };
    return { ok: false, message: authMessage(response.status, body.error) };
  }

  // Read the profile the same way every other caller does, so there is exactly
  // one shape of `Me` in the client.
  const me = await fetchMe();
  if (!me) return { ok: false, message: 'Signed in, but the session did not stick.' };
  return { ok: true, me };
}

export function emailRegister(email: string, password: string): Promise<AuthResult> {
  return postCredentials('/auth/email/register', email, password);
}

export function emailLogin(email: string, password: string): Promise<AuthResult> {
  return postCredentials('/auth/email/login', email, password);
}

export async function startGuest(): Promise<Me | null> {
  const response = await fetch('/auth/guest', { method: 'POST', credentials: 'same-origin' });
  if (!response.ok) throw new Error(`/auth/guest failed with ${response.status}`);
  // The route returns identity only; read the full profile the same way every
  // other caller does, so there is exactly one shape of `Me` in the client.
  return fetchMe();
}
